<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Relatório resumo de envio</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Acompanhe o resumo diário de conversas, envios massivos, entregas, leituras e respostas.</p>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white px-5 py-3 shadow-sm">
          <p class="text-xs font-medium uppercase tracking-wider text-gray-400">Conversas totais</p>
          <p class="mt-1 text-2xl font-semibold text-blue-700">
            {{ Number(relatorio.total || 0).toLocaleString("pt-br") }}
          </p>
        </div>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="p-6">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div class="flex flex-col gap-1">
              <label for="data" class="text-sm font-medium text-gray-500"> Acompanhar data </label>
              <input
                v-model="form.data"
                type="date"
                id="data"
                class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="relatorio.chats.length" class="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div class="lg:col-span-4">
          <div class="flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div class="border-b border-gray-100 px-6 py-5">
              <h2 class="text-lg font-semibold text-gray-900">Conversas</h2>
              <p class="mt-1 text-sm text-gray-500">Filtrando {{ moment(form.data).format("DD/MM") }}</p>
            </div>

            <div class="flex flex-1 items-center justify-center px-4 py-6">
              <div class="w-full max-w-[320px]">
                <apexchart type="donut" height="280" :options="optionsConversas" :series="seriesConversas" />
              </div>
            </div>
          </div>
        </div>

        <div class="lg:col-span-8">
          <div class="flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div class="border-b border-gray-100 px-6 py-5">
              <h2 class="text-lg font-semibold text-gray-900">Conversas por horário</h2>
              <p class="mt-1 text-sm text-gray-500">Distribuição de conversas ao longo do dia.</p>
            </div>

            <div class="flex flex-1 items-center px-4 py-6">
              <div class="h-[280px] w-full">
                <apexchart type="bar" height="280" :options="optionsHorarios" :series="seriesHorarios" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="space-y-3">
        <div>
          <h2 class="text-2xl font-semibold text-gray-900">Informações conversas</h2>
          <p class="mt-1 text-sm text-gray-500"> Conversas iniciadas manualmente.</p>
        </div>

        <div class="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div class="overflow-x-auto">
            <table class="w-full min-w-max text-sm">
              <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
                <tr>
                  <th class="px-6 py-4 text-left font-medium">Centro de custo</th>
                  <th class="px-6 py-4 text-left font-medium">Total</th>
                  <th class="px-6 py-4 text-left font-medium">Sem Whatsapp</th>
                  <th class="px-6 py-4 text-left font-medium">{{ $store.getters.rotuloEnvio('Enviados', 'Enviados para fila') }}</th>
                  <th class="px-6 py-4 text-left font-medium">Entregues</th>
                  <th class="px-6 py-4 text-left font-medium">Lidos</th>
                  <th class="px-6 py-4 text-left font-medium">Respondidos</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="el in relatorio.chats" :key="el.centrocusto" class="border-b border-gray-100 transition hover:bg-slate-50">
                  <td class="px-6 py-5">
                    <div class="flex items-center gap-4">
                      <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700">
                        {{ (el.centrocusto || "SC").slice(0, 2).toUpperCase() }}
                      </div>

                      <div class="flex flex-col">
                        <span class="font-medium text-gray-800">
                          {{ el.centrocusto || "SEM CENTRO DE CUSTO" }}
                        </span>
                        <span class="text-xs text-gray-400">Conversas registradas</span>
                      </div>
                    </div>
                  </td>

                  <td class="px-6 font-medium text-gray-800">
                    {{ Number(el.totalIniciado || 0).toLocaleString("pt-br") }}
                  </td>

                  <td class="px-6">
                    <span class="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                      {{ Number(el.semWhatsapp || 0).toLocaleString("pt-br") }}
                    </span>
                  </td>

                  <td class="px-6">
                    <span class="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                      {{ Number(el.enviados || 0).toLocaleString("pt-br") }}
                    </span>
                  </td>

                  <td class="px-6">
                    <span class="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                      {{ Number(el.entregues || 0).toLocaleString("pt-br") }}
                    </span>
                  </td>

                  <td class="px-6">
                    <span class="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-700">
                      {{ Number(el.lidos || 0).toLocaleString("pt-br") }}
                    </span>
                  </td>

                  <td class="px-6">
                    <span class="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                      {{ Number(el.respondidos || 0).toLocaleString("pt-br") }}
                    </span>
                  </td>
                </tr>

                <tr v-if="relatorio.chats.length === 0">
                  <td colspan="7" class="px-6 py-12 text-center text-sm text-gray-400">Nenhuma informação de conversa encontrada para a data selecionada.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="space-y-3">
        <div>
          <h2 class="text-2xl font-semibold text-gray-900">Informações massivo</h2>
          <p class="mt-1 text-sm text-gray-500">Resumo dos uploads e envios para fila de disparo.</p>
        </div>

        <div class="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div class="overflow-x-auto">
            <table class="w-full min-w-max text-sm">
              <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
                <tr>
                  <th class="px-6 py-4 text-left font-medium">Centro de custo</th>
                  <th class="px-6 py-4 text-left font-medium">Total Upload</th>
                  <th class="px-6 py-4 text-left font-medium">Sem Whatsapp</th>
                  <th class="px-6 py-4 text-left font-medium">{{ $store.getters.rotuloEnvio('Enviado para fila de disparo', 'Enviado para fila') }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="el in relatorio.massivo" :key="el.centrocusto" class="border-b border-gray-100 transition hover:bg-slate-50">
                  <td class="px-6 py-5">
                    <div class="flex items-center gap-4">
                      <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700">
                        {{ (el.centrocusto || "SC").slice(0, 2).toUpperCase() }}
                      </div>

                      <div class="flex flex-col">
                        <span class="font-medium text-gray-800">
                          {{ el.centrocusto || "SEM CENTRO DE CUSTO" }}
                        </span>
                        <span class="text-xs text-gray-400">Envio massivo</span>
                      </div>
                    </div>
                  </td>

                  <td class="px-6 font-medium text-gray-800">
                    {{ Number(el.totalUpload || 0).toLocaleString("pt-br") }}
                  </td>

                  <td class="px-6">
                    <span class="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                      {{ Number(el.semWhatsapp || 0).toLocaleString("pt-br") }}
                    </span>
                  </td>

                  <td class="px-6">
                    <span class="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                      {{ Number(el.totalEnviadoFila || 0).toLocaleString("pt-br") }}
                    </span>
                  </td>
                </tr>

                <tr v-if="relatorio.massivo.length === 0">
                  <td colspan="4" class="px-6 py-12 text-center text-sm text-gray-400">Nenhuma informação de envio massivo encontrada para a data selecionada.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="relatorio.chats.length" class="flex min-h-[170px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="border-b border-gray-100 px-6 py-5">
          <h2 class="text-lg font-semibold text-gray-900">Etiquetas / Tags</h2>
          <p class="mt-1 text-sm text-gray-500">Distribuição das etiquetas utilizadas nas conversas.</p>
        </div>

        <div class="flex flex-1 items-center px-4 py-2">
          <div class="h-[90px] w-full">
            <apexchart type="treemap" height="90" :options="optionTags" :series="seriesTags" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { colorMixin } from "@/utils/colorMixin";

function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") {
    return null;
  }

  const normalized = hex.replace("#", "");

  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(normalized)) {
    return null;
  }

  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;

  return {
    red: parseInt(expanded.slice(0, 2), 16),
    green: parseInt(expanded.slice(2, 4), 16),
    blue: parseInt(expanded.slice(4, 6), 16)
  };
}

function mixHexColors(baseHex, targetHex, amount) {
  const base = hexToRgb(baseHex);
  const target = hexToRgb(targetHex);

  if (!base || !target) {
    return baseHex || targetHex;
  }

  const mix = (start, end) => Math.round(start + (end - start) * amount);

  return `#${[mix(base.red, target.red), mix(base.green, target.green), mix(base.blue, target.blue)].map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function buildTintPalette(baseHex, count) {
  const palette = [];

  for (let index = 0; index < count; index += 1) {
    const ratio = count <= 1 ? 0 : index / (count - 1);
    const target = ratio < 0.7 ? "#ffffff" : "#111827";
    const amount = ratio < 0.7 ? (ratio / 0.7) * 0.42 : ((ratio - 0.7) / 0.3) * 0.22;

    palette.push(mixHexColors(baseHex, target, amount));
  }

  return palette;
}

const apexChartBase = {
  chart: {
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    toolbar: {
      show: false
    }
  },
  colors: ["#2563eb", "#10b981", "#f59e0b", "#ef4444"],
  dataLabels: {
    enabled: false
  },
  stroke: {
    lineCap: "round"
  },
  legend: {
    fontSize: "13px",
    fontWeight: 500,
    itemMargin: {
      horizontal: 10,
      vertical: 4
    },
    labels: {
      colors: "#6b7280"
    },
    markers: {
      width: 10,
      height: 10,
      radius: 999
    }
  },
  tooltip: {
    theme: "light",
    style: {
      fontSize: "13px"
    }
  }
};

const apexGrid = {
  borderColor: "#e5e7eb",
  strokeDashArray: 4
};

export default {
  mixins: [colorMixin],
  data() {
    return {
      route: "relatorio-envio",
      moment,
      form: {
        data: moment().startOf("day").format("YYYY-MM-DD")
      },
      seriesConversas: [0, 0, 0, 0],
      seriesTags: [
        {
          data: []
        }
      ],
      optionsConversasBase: {
        ...apexChartBase,
        chart: {
          ...apexChartBase.chart,
          height: 280,
          id: "chartGeral",
          type: "donut"
        },
        plotOptions: {
          pie: {
            donut: {
              size: "62%"
            }
          }
        },
        legend: {
          ...apexChartBase.legend,
          position: "bottom",
          horizontalAlign: "center",
          markers: {
            width: 9,
            height: 9,
            radius: 999
          }
        }
      },
      optionTags: {
        ...apexChartBase,
        legend: {
          show: false
        },
        chart: {
          ...apexChartBase.chart,
          height: 90,
          type: "treemap"
        },
        colors: buildTintPalette("#2563eb", 12),
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: false
          }
        },
        tooltip: {
          ...apexChartBase.tooltip,
          y: {
            formatter(value) {
              return Number(value || 0).toLocaleString("pt-br");
            }
          }
        }
      },
      seriesHorarios: [
        {
          name: "Conversas",
          data: []
        }
      ],
      optionsHorarios: {
        ...apexChartBase,
        chart: {
          ...apexChartBase.chart,
          height: 280,
          type: "bar"
        },
        plotOptions: {
          bar: {
            columnWidth: "42%",
            borderRadius: 8,
            borderRadiusApplication: "end"
          }
        },
        grid: {
          ...apexGrid,
          padding: {
            left: 4,
            right: 8
          },
          xaxis: {
            lines: {
              show: false
            }
          }
        },
        xaxis: {
          labels: {
            style: {
              colors: "#6b7280",
              fontSize: "12px",
              fontWeight: 600
            }
          },
          axisBorder: {
            color: "#e5e7eb"
          },
          axisTicks: {
            color: "#e5e7eb"
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: "#6b7280",
              fontSize: "12px"
            }
          }
        },
        colors: ["#2563eb"],
        tooltip: {
          ...apexChartBase.tooltip,
          y: {
            formatter(value) {
              return Number(value || 0).toLocaleString("pt-br");
            }
          }
        }
      },
      relatorio: {
        massivo: [],
        chats: [],
        total: 0
      }
    };
  },

  computed: {
    optionsConversas() {
      return {
        ...this.optionsConversasBase,
        labels: [this.$store.getters.rotuloEnvio("Enviados", "Enviados para fila"), "Entregues", "Lidos", "Sem Whatsapp"]
      };
    },
    primaryColor() {
      return this.$store.state.colorPrincipal || "#2f4f4f";
    }
  },

  created() {
    this.syncChartColors();
  },

  watch: {
    primaryColor() {
      this.syncChartColors();
    },

    "form.data"() {
      this.list();
    }
  },

  methods: {
    syncChartColors() {
      const primaryColor = this.primaryColor;

      this.optionsConversasBase = {
        ...this.optionsConversasBase,
        colors: [primaryColor, mixHexColors(primaryColor, "#ffffff", 0.22), mixHexColors(primaryColor, "#111827", 0.12), mixHexColors(primaryColor, "#ffffff", 0.42)]
      };

      this.optionsHorarios = {
        ...this.optionsHorarios,
        colors: [primaryColor]
      };

      this.optionTags = {
        ...this.optionTags,
        colors: buildTintPalette(primaryColor, 12)
      };
    },

    async list() {
      const loader = this.$loading.show({
        container: null,
        canCancel: true
      });

      const listReq = await this.$http.post("/v1/chatV2/resumo-envios", this.form);

      if (!listReq.data || !listReq.data.grafico) {
        loader.hide();
        return;
      }

      this.relatorio = {
        massivo: listReq.data.massivo || [],
        chats: listReq.data.chats || [],
        total: listReq.data.total || 0
      };

      this.seriesConversas = [listReq.data.grafico.conversas.enviados || 0, listReq.data.grafico.conversas.entregues || 0, listReq.data.grafico.conversas.lidos || 0, listReq.data.grafico.conversas.semWhatsapp || 0];

      this.seriesTags = [
        {
          data: listReq.data.grafico.tags || []
        }
      ];

      this.seriesHorarios = [
        {
          name: "Conversas",
          data: listReq.data.horarios || []
        }
      ];

      loader.hide();
    }
  },

  async beforeMount() {
    await this.list();
  }
};
</script>
